import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { useNotification } from 'spindl-shared-components';

import { copyTextToClipboard } from '../../../lib/utils';
import { DESKTOP_MEDIUM, NOTIFICATION_DURATION } from '../../../lib/constants';
import { useWindowSize } from '../../../lib/useWindowSize';
import styles from './UserRow.module.scss';
import getProps from './UserRow.props';
import { UserRowCombinedProps } from './types';

const usePresenter = (props: UserRowCombinedProps): UserRowCombinedProps => {
  const internalProps = getProps('');
  const { t } = useTranslation();
  const { trigger } = useNotification();
  const { width } = useWindowSize();
  const [offset, setOffset] = useState<[number, number]>((width >= DESKTOP_MEDIUM) ? [-100, -20] : [-60, -20]);
  const { scrollContainerRef } = props;

  const copyEmailAddress = async (email: string) => {
    await copyTextToClipboard(email);

    trigger({
      message: t('toast_notification.copy_email'),
      type: 'Positive',
      duration: NOTIFICATION_DURATION,
    });
  };

  useEffect(() => {
    if (width >= DESKTOP_MEDIUM) {
      setOffset([-100, -20]);
    } else {
      setOffset([-60, -20]);
    }
  }, [width]);

  return {
    ...props,
    name: {
      targetProps: {
        className: cx(styles.name, props.classes?.name),
        ...internalProps.name,
        ...props.name?.targetProps,
      },
    },
    email: {
      targetProps: {
        className: cx(styles.email, props.classes?.email),
        ...internalProps.email,
        ...props.email?.targetProps,
      },
      action: 'hover',
      placement: 'top',
      showDelay: 1,
      hideDelay: 1,
      allowHoverDuringScroll: false,
      scrollContainerRef: scrollContainerRef,
      containerRef: scrollContainerRef,
      contentProps: {
        type: 'CopyIcon',
        size: 'S',
        styleType: 'PrimarySubdued',
        state: 'Default',
        onClick: () => props.email?.targetProps?.text?.value && copyEmailAddress(String(props.email.targetProps.text.value)),
      },
      offset: offset,
    },
    role: {
      targetProps: {
        className: cx(styles.role, props.classes?.role),
        ...internalProps.role,
        ...props.role?.targetProps,
      },
    },
    time: {
      targetProps: {
        className: cx(styles.time, props.classes?.time),
        ...internalProps.time,
        ...props.time?.targetProps,
      },
    },
    date: {
      targetProps: {
        className: cx(styles.date, props.classes?.date),
        ...internalProps.date,
        ...props.date?.targetProps,
      },
    },
    status: {
      targetProps: {
        className: cx(styles.status, props.classes?.status),
        ...internalProps.status,
        ...props.status?.targetProps,
      },
    },
    button: {
      targetProps: {
        className: cx(styles.button, props.classes?.button),
        ...internalProps.button,
        ...props.button?.targetProps,
      },
    },
  };
};

export default usePresenter;
